"use client";

import React, { useEffect, useState } from 'react';
import { HelpCircle, Loader2, Play, Power, Save, Sparkles, Trash2 } from 'lucide-react';
import Dialog from '../ui/Dialog';
import {
  EMPTY_SOURCE_FORM,
  NORMALIZE_MODE_OPTIONS,
  SOURCE_FIELD_HELP,
  createSource,
  deleteSource,
  ensureCloudTrailStarter,
  sourceToForm,
  testSourceIngest,
  updateSource,
  updateSourceStatus,
  type SourceFieldKey,
  type SourceFormFields,
} from '../api-utils/sources/manage_sources';
import type { SourceItem } from '../api-utils/sources';

const fieldClass =
  'w-full rounded-lg border border-c3-tile-bd bg-s2 px-3 py-2 text-sm text-c3-white outline-none focus:border-c3-purple placeholder:text-c3-mute disabled:opacity-50';

type ConnectSourceModalProps = {
  open: boolean;
  source?: SourceItem | null;
  /** The application picked from the menu of available sources; a new source starts with its name and provider. */
  preset?: { provider: string; name: string } | null;
  onClose: () => void;
  onChanged: () => void;
};

/** A new source for an application picked from the menu: its name and provider in, the rest as for any new source. */
function presetToForm(preset: { provider: string; name: string }): SourceFormFields {
  return {
    ...EMPTY_SOURCE_FORM,
    name: preset.name,
    provider: preset.provider,
    candidate_source: preset.provider,
    prefix: `candidates/${preset.provider}/`,
    normalize_mode: preset.provider === 'aws' ? EMPTY_SOURCE_FORM.normalize_mode : 'pre_normalized',
  };
}

const ConnectSourceModal: React.FC<ConnectSourceModalProps> = ({
  open,
  source,
  preset,
  onClose,
  onChanged,
}) => {
  const selectedId = source?._id || null;
  const [form, setForm] = useState<SourceFormFields>({ ...EMPTY_SOURCE_FORM });
  const [helpKey, setHelpKey] = useState<SourceFieldKey>('overview');
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setForm(source || !preset ? sourceToForm(source) : presetToForm(preset));
    setHelpKey('overview');
    setError(null);
    setMessage(null);
    setTestResult(null);
    // Reset only when opening or switching source id — not on poll refresh of the same doc.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: source?._id and the preset's provider only
  }, [open, source?._id, preset?.provider]);

  if (!open) return null;

  const setField = (key: keyof SourceFormFields, value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const help = SOURCE_FIELD_HELP[helpKey];

  const save = async () => {
    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      if (selectedId) {
        await updateSource(selectedId, form);
        setMessage('Source updated');
      } else {
        await createSource(form);
        setMessage('Source created');
      }
      onChanged();
      if (!selectedId) onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async () => {
    if (!selectedId) return;
    const next = (form.status || '').toLowerCase() === 'active' ? 'inactive' : 'active';
    if (
      next === 'active' &&
      !window.confirm(
        'Activating a source means the system scans only your connected sources and stops reading the old default location. Continue?'
      )
    ) {
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await updateSourceStatus(selectedId, next);
      setForm((prev) => ({ ...prev, status: next }));
      setMessage(`Status → ${next}`);
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!selectedId) return;
    if (
      !window.confirm(
        `Delete source "${form.name || selectedId}"? This does not remove S3 objects or candidates.`
      )
    ) {
      return;
    }
    setDeleting(true);
    setError(null);
    try {
      await deleteSource(selectedId);
      onChanged();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setDeleting(false);
    }
  };

  const testIngest = async () => {
    if (!selectedId) {
      setError('Save the source first, then run Test ingest');
      return;
    }
    setTesting(true);
    setError(null);
    setTestResult(null);
    try {
      const result = await testSourceIngest(selectedId);
      setTestResult(JSON.stringify(result.raw, null, 2));
      if (!result.ok) {
        throw new Error(result.lastStatus || 'Test ingest failed');
      }
      setMessage(result.lastStatus || 'Test ingest OK');
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setTesting(false);
    }
  };

  const seedStarter = async () => {
    setSeeding(true);
    setError(null);
    try {
      const result = await ensureCloudTrailStarter();
      setMessage(result.message);
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSeeding(false);
    }
  };

  return (
    <Dialog
      size="xl"
      eyebrow="Log sources"
      title={selectedId ? 'Edit source' : 'Connect new source'}
      onClose={onClose}
    >
      <p className="mb-5 border-b border-c3-tile-bd pb-4 text-sm text-c3-mute">
        Configure an S3 drop zone for ingest. Active sources are scanned for new alerts.
      </p>

      {error && (
        <div role="alert" className="mb-3 rounded-lg border border-c3-red/40 bg-c3-red/10 px-3 py-2 text-sm text-c3-red">
          {error}
        </div>
      )}
      {message && (
        <div role="status" className="mb-3 rounded-lg border border-c3-tile-bd bg-c3-tile/60 px-3 py-2 text-sm text-c3-white">
          {message}
        </div>
      )}

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-[1.4fr_0.9fr]">
        <div className="space-y-3">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field
              label="Name"
              field="name"
              helpKey={helpKey}
              onFocusHelp={setHelpKey}
            >
              <input
                className={fieldClass}
                value={form.name}
                onChange={(e) => setField('name', e.target.value)}
                onFocus={() => setHelpKey('name')}
              />
            </Field>
            <Field
              label="Provider"
              field="provider"
              helpKey={helpKey}
              onFocusHelp={setHelpKey}
            >
              <input
                className={fieldClass}
                value={form.provider}
                onChange={(e) => setField('provider', e.target.value)}
                onFocus={() => setHelpKey('provider')}
                placeholder="aws, paloalto, …"
              />
            </Field>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field
              label="Normalize mode"
              field="normalize_mode"
              helpKey={helpKey}
              onFocusHelp={setHelpKey}
            >
              <select
                className={fieldClass}
                value={form.normalize_mode}
                onChange={(e) => {
                  setField('normalize_mode', e.target.value);
                  setHelpKey('normalize_mode');
                }}
                onFocus={() => setHelpKey('normalize_mode')}
              >
                {NORMALIZE_MODE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </Field>
            <Field
              label="Candidate source slug"
              field="candidate_source"
              helpKey={helpKey}
              onFocusHelp={setHelpKey}
            >
              <input
                className={fieldClass}
                value={form.candidate_source}
                onChange={(e) => setField('candidate_source', e.target.value)}
                onFocus={() => setHelpKey('candidate_source')}
                placeholder="defaults to provider"
              />
            </Field>
          </div>

          <Field label="Bucket" field="bucket" helpKey={helpKey} onFocusHelp={setHelpKey}>
            <input
              className={fieldClass}
              value={form.bucket}
              onChange={(e) => setField('bucket', e.target.value)}
              onFocus={() => setHelpKey('bucket')}
            />
          </Field>
          <Field label="Prefix" field="prefix" helpKey={helpKey} onFocusHelp={setHelpKey}>
            <input
              className={fieldClass}
              value={form.prefix}
              onChange={(e) => setField('prefix', e.target.value)}
              onFocus={() => setHelpKey('prefix')}
              placeholder="splunk_dates/ or candidates/aws/"
            />
          </Field>
          <Field
            label="Prefix filter"
            field="prefix_filter"
            helpKey={helpKey}
            onFocusHelp={setHelpKey}
          >
            <input
              className={fieldClass}
              value={form.prefix_filter}
              onChange={(e) => setField('prefix_filter', e.target.value)}
              onFocus={() => setHelpKey('prefix_filter')}
              placeholder="export_paloalto (optional)"
            />
          </Field>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field label="Region" field="region" helpKey={helpKey} onFocusHelp={setHelpKey}>
              <input
                className={fieldClass}
                value={form.region}
                onChange={(e) => setField('region', e.target.value)}
                onFocus={() => setHelpKey('region')}
              />
            </Field>
            <Field
              label="Object filter"
              field="object_filter"
              helpKey={helpKey}
              onFocusHelp={setHelpKey}
            >
              <input
                className={fieldClass}
                value={form.object_filter}
                onChange={(e) => setField('object_filter', e.target.value)}
                onFocus={() => setHelpKey('object_filter')}
                placeholder="*.json or *.csv"
              />
            </Field>
          </div>

          <Field
            label="Assume role ARN (optional)"
            field="assume_role_arn"
            helpKey={helpKey}
            onFocusHelp={setHelpKey}
          >
            <input
              className={fieldClass}
              value={form.assume_role_arn}
              onChange={(e) => setField('assume_role_arn', e.target.value)}
              onFocus={() => setHelpKey('assume_role_arn')}
              placeholder="not auto-assumed yet"
            />
          </Field>
          <Field
            label="Adapter URI (module only)"
            field="adapter_uri"
            helpKey={helpKey}
            onFocusHelp={setHelpKey}
          >
            <input
              className={fieldClass}
              value={form.adapter_uri}
              onChange={(e) => setField('adapter_uri', e.target.value)}
              onFocus={() => setHelpKey('adapter_uri')}
              disabled={form.normalize_mode !== 'module'}
            />
          </Field>

          <div className="flex flex-wrap gap-2 pt-2">
            <button
              type="button"
              className="c3-btn c3-btn-primary c3-btn-sm inline-flex items-center gap-2"
              onClick={() => void save()}
              disabled={saving}
            >
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
              Save
            </button>
            <button
              type="button"
              className="c3-btn c3-btn-ghost c3-btn-sm inline-flex items-center gap-2"
              onClick={() => void toggleActive()}
              disabled={!selectedId || saving}
            >
              <Power className="h-3.5 w-3.5" />
              {(form.status || '').toLowerCase() === 'active' ? 'Deactivate' : 'Activate'}
            </button>
            <button
              type="button"
              className="c3-btn c3-btn-ghost c3-btn-sm inline-flex items-center gap-2"
              onClick={() => void testIngest()}
              disabled={!selectedId || testing}
            >
              {testing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5" />}
              Test ingest
            </button>
            {selectedId && (
              <button
                type="button"
                className="c3-btn c3-btn-ghost c3-btn-sm inline-flex items-center gap-2 text-c3-red"
                onClick={() => void remove()}
                disabled={deleting || saving}
              >
                {deleting ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Trash2 className="h-3.5 w-3.5" />
                )}
                Delete
              </button>
            )}
            {!selectedId && (
              <button
                type="button"
                className="c3-btn c3-btn-ghost c3-btn-sm inline-flex items-center gap-2"
                onClick={() => void seedStarter()}
                disabled={seeding}
              >
                {seeding ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Sparkles className="h-3.5 w-3.5" />
                )}
                Add CloudTrail starter
              </button>
            )}
          </div>

          {selectedId && (
            <p className="text-[12px] text-c3-mute">
              Status:{' '}
              <span className="text-c3-white">{form.status || 'inactive'}</span>
              {source?.last_status ? ` · Last: ${source.last_status}` : ''}
            </p>
          )}

          {testResult && (
            <pre className="max-h-40 overflow-auto rounded-lg border border-c3-tile-bd bg-s2 p-2 text-[12px] text-c3-mute whitespace-pre-wrap">
              {testResult}
            </pre>
          )}
        </div>

        <aside className="rounded-xl border border-c3-tile-bd bg-c3-tile/40 p-4 h-fit">
          <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-c3-white">
            <HelpCircle className="h-4 w-4 text-c3-purple" />
            Field guide
          </div>
          <button
            type="button"
            className="mb-3 text-xs text-c3-mute underline underline-offset-2 hover:text-c3-white"
            onClick={() => setHelpKey('overview')}
          >
            Show overview
          </button>
          <h4 className="text-sm font-bold text-c3-white">{help.title}</h4>
          <p className="mt-2 text-sm leading-relaxed text-c3-mute">{help.body}</p>
          <ul className="mt-4 space-y-1 border-t border-c3-tile-bd pt-3 text-xs text-c3-mute">
            <li>Focus a field to update this panel.</li>
            <li>Save first, then Activate / Test ingest.</li>
          </ul>
        </aside>
      </div>
    </Dialog>
  );
};

function Field({
  label,
  field,
  children,
  onFocusHelp,
}: {
  label: string;
  field: SourceFieldKey;
  helpKey: SourceFieldKey;
  children: React.ReactNode;
  onFocusHelp: (key: SourceFieldKey) => void;
}) {
  return (
    <div className="space-y-1">
      <div className="flex items-center gap-1.5">
        <label
          className="cursor-pointer text-xs font-semibold uppercase tracking-wide text-c3-mute"
          onClick={() => onFocusHelp(field)}
        >
          {label}
        </label>
        <button
          type="button"
          className="text-c3-mute hover:text-c3-white"
          onClick={() => onFocusHelp(field)}
          aria-label={`Help: ${label}`}
        >
          <HelpCircle className="h-3.5 w-3.5" />
        </button>
      </div>
      {children}
    </div>
  );
}

export default ConnectSourceModal;
